import { useCallback, useEffect, useRef, useState } from 'react';
import { positionToGeo, type StopGeo } from '@ecobox/firebase';

export type GeoState = 'idle' | 'asking' | 'ok' | 'denied' | 'unavailable';

/** One position fix from the phone, or undefined. Never throws: location is always optional. */
export function readPosition(timeoutMs = 8000): Promise<StopGeo | undefined> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return Promise.resolve(undefined);
  return new Promise((resolve) => {
    let settled = false;
    const done = (geo: StopGeo | undefined): void => {
      if (settled) return;
      settled = true;
      resolve(geo);
    };
    // Some phones never answer when GPS has no sky: do not hold up a delivery for it.
    const timer = setTimeout(() => done(undefined), timeoutMs + 500);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        clearTimeout(timer);
        done(positionToGeo(position));
      },
      () => {
        clearTimeout(timer);
        done(undefined);
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 30_000 },
    );
  });
}

const FRESH_MS = 2 * 60_000;

/**
 * The phone's position for a stop. A fix is asked for when the screen opens (GPS works with no
 * signal); `fix()` hands over the latest one, or asks again if it is older than two minutes. A
 * person who says no, or a phone with no GPS, simply gets no position: the command goes without.
 */
export function useGeo(auto = true): {
  geo: StopGeo | undefined;
  state: GeoState;
  fix: () => Promise<StopGeo | undefined>;
} {
  const [geo, setGeo] = useState<StopGeo | undefined>();
  const [state, setState] = useState<GeoState>('idle');
  const latest = useRef<{ geo: StopGeo; at: number } | undefined>(undefined);

  const fix = useCallback(async (): Promise<StopGeo | undefined> => {
    const have = latest.current;
    if (have && Date.now() - have.at < FRESH_MS) return have.geo;
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setState('unavailable');
      return undefined;
    }
    setState('asking');
    const found = await readPosition();
    if (found) {
      latest.current = { geo: found, at: Date.now() };
      setGeo(found);
      setState('ok');
    } else {
      setState((s) => (s === 'ok' ? s : 'denied'));
    }
    return found ?? latest.current?.geo;
  }, []);

  useEffect(() => {
    if (!auto) return;
    // Asks once when the screen opens; the answer arrives later and is kept.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fix();
  }, [auto, fix]);

  return { geo, state, fix };
}
